<template>
    <div class="manage-pm-modal">
        <header class="mpm-header">
            <div class="mpm-title">
                <span class="mpm-icon"><CreditCardIcon class="w-5 h-5 text-gray-600" /></span>
                <div>
                    <p class="mpm-heading">Payment methods</p>
                    <p class="mpm-sub">{{ subheading }}</p>
                </div>
            </div>
            <button type="button" class="mpm-close" aria-label="Close" :disabled="busy" autofocus @click="closeDialog">
                <XMarkIcon class="w-5 h-5" aria-hidden="true" />
            </button>
        </header>

        <div class="mpm-body">
            <p v-if="!hasLoaded && !loadFailed" class="mpm-sub" data-testid="pm-loading">Loading saved cards…</p>

            <div v-else-if="loadFailed" role="alert" class="mpm-load-error" data-testid="pm-load-error">
                <p>We couldn't load {{ isCompany ? `${companyName}'s` : 'your' }} saved cards.</p>
                <button type="button" class="mpm-retry" :disabled="busy" @click="retryLoad">Try again</button>
            </div>

            <PaymentMethodsPanel
                v-else
                embedded
                mode="settings"
                :methods="sortedMethods"
                :at-cap="atCap"
                :busy="busy"
                :hide-add="adding || !!editingId || noCardsOnFile"
                @set-primary="onSetPrimary"
                @edit="onEdit"
                @delete="onDelete"
                @add="onAdd"
            />

            <!-- Inline edit (D-6/D-14): only expiry + cardholder name; the card number is never editable. -->
            <div v-if="editingId && hasLoaded && !loadFailed" class="mpm-form" data-testid="pm-edit-form">
                <p class="mpm-form-title">Edit card</p>
                <div class="mpm-grid">
                    <ModalField label="Exp. month" input-id="mpm-edit-month">
                        <input id="mpm-edit-month" ref="editMonthEl" v-model.number="editMonth" type="number" min="1" max="12" class="mpm-input">
                    </ModalField>
                    <ModalField label="Exp. year" input-id="mpm-edit-year">
                        <input id="mpm-edit-year" v-model.number="editYear" type="number" min="2000" max="2100" class="mpm-input">
                    </ModalField>
                    <ModalField label="Cardholder" input-id="mpm-edit-name">
                        <input id="mpm-edit-name" v-model="editName" type="text" placeholder="Name on card" class="mpm-input">
                    </ModalField>
                </div>
                <div class="mpm-actions">
                    <PrimeButton label="Save changes" :loading="busy" @click="onSaveEdit" />
                    <PrimeButton label="Cancel" class="p-btn-root_secondary" :disabled="busy" @click="editingId = null" />
                </div>
            </div>

            <!-- Inline add card. -->
            <div v-if="adding && hasLoaded && !loadFailed" class="mpm-form" data-testid="pm-add-form">
                <p class="mpm-form-title">Add a card</p>
                <div class="mpm-grid">
                    <ModalField label="First name" input-id="mpm-add-first">
                        <input id="mpm-add-first" v-model="addFirstName" type="text" placeholder="John" class="mpm-input">
                    </ModalField>
                    <ModalField label="Last name" input-id="mpm-add-last">
                        <input id="mpm-add-last" v-model="addLastName" type="text" placeholder="Doe" class="mpm-input">
                    </ModalField>
                </div>
                <ModalField label="Card details" :error="addCardError || undefined">
                    <div v-if="!isDemoMode()" id="manage-pm-card-element" class="mpm-card-element"></div>
                    <p v-else class="mpm-sub" data-testid="pm-demo-note">Demo mode — no real card required.</p>
                </ModalField>
                <div class="mpm-actions">
                    <PrimeButton label="Add card" :loading="busy" :disabled="!canSubmitNewCard" @click="onSaveNewCard" />
                    <!-- With no card on file there is nothing to cancel back to: the Add button is
                         gone too, so Cancel would leave an empty body and no way to add. The close
                         X is the way out of that state. -->
                    <PrimeButton v-if="!noCardsOnFile" label="Cancel" class="p-btn-root_secondary" :disabled="busy" @click="cancelAdd" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button';
import { CreditCardIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import PaymentMethodsPanel from '~/components/billing/PaymentMethodsPanel.vue';
import ModalField from '~/components/modals/shared/ModalField.vue';
import { usePaymentMethods, type PaymentMethod } from '~/composables/usePaymentMethods';
import { isDemoMode } from '~/services/demo/mode';
import { useAuthStore } from '~/stores/auth';
import { usePersonStore } from '~/stores/person';
import { ModalNames } from '~/types/modals';
import { memberFacingMessage } from '~/utils/errors';

const CAP = 5;

const dialogRef = getDialogRef(ModalNames.MANAGE_PAYMENT_METHODS);
const authStore = useAuthStore();
const personStore = usePersonStore();
const config = useRuntimeConfig();
const { showSuccess, showError } = useGlobalToast();

// Whose cards these are. A `companyId` in the dialog data opens the COMPANY's
// collection — the cards the business is billed on, shared with every other
// financial admin — and without it the modal is the person's own wallet. The two
// differ in what they say and in what they refresh afterwards; the card
// mechanics below are identical because the two collections are.
const companyId = computed(() => dialogRef.value?.data?.companyId as number | undefined);
const isCompany = computed(() => !!companyId.value);
const companyName = computed(() => (dialogRef.value?.data?.companyName as string | undefined) ?? 'this company');

const ownerScope = computed<'people' | 'companies'>(() => (isCompany.value ? 'companies' : 'people'));
const ownerId = computed(() => (isCompany.value ? companyId.value : personStore.person?.id));

const subheading = computed(() =>
    isCompany.value
        ? `Cards ${companyName.value} is billed on. The primary is charged first; backups are tried automatically if it fails.`
        : 'Your primary card is charged first; backups are tried automatically if it fails.',
);

// The scope is fixed for the life of one dialog instance (the data is frozen),
// so reading it once here is safe and keeps every request on one payer.
const { methods, sortedMethods, error: methodsError, loading: methodsLoading, fetch: fetchPaymentMethods, setPrimary, edit, remove, add } =
    usePaymentMethods(() => ownerId.value, ownerScope.value);

/**
 * A 403/500 on the wallet read resolves — the composable records the reason
 * rather than throwing — so without this the modal renders an empty collection
 * with an Add button and no hint that cards exist but could not be loaded. That
 * misreads worst for a company, whose cards belong to the whole account.
 */
const loadFailed = computed(() => methodsError.value !== null && !methodsLoading.value);

/**
 * True only once the first read has actually returned. Until then the collection
 * is empty for want of an answer, not because the account has no cards — and
 * offering Add against that empty list lets an admin fill in a card for a company
 * already at the cap, only to have the attach refused after they typed it.
 */
const hasLoaded = ref(false);
const busy = ref(false);
// Set when a company's cards changed, so the closing payload tells the billing
// page its summary (primary card, has_payment_method) is now stale.
const cardsChanged = ref(false);
const atCap = computed(() => methods.value.length >= CAP); // cap check uses the raw list (count only)

/**
 * The wallet has answered and holds nothing. An empty wallet has no list to show
 * and no card to manage, so the add-card form is the body: the "+ Add payment
 * method" button would be a step with one option. Gated on the read having
 * landed — while it is loading, and when it failed, the list is empty for want of
 * an answer and the panel isn't on screen at all. An empty wallet is 0 of 5, so
 * this state can never be at the cap that `onAdd` refuses.
 */
const noCardsOnFile = computed(() => hasLoaded.value && !loadFailed.value && methods.value.length === 0);

const editingId = ref<string | null>(null);
const editMonthEl = ref<HTMLInputElement | null>(null);
const editMonth = ref<number | null>(null);
const editYear = ref<number | null>(null);
const editName = ref('');

const adding = ref(false);
/**
 * A card Stripe already confirmed whose attach did not land. A SetupIntent can
 * only be confirmed once, so Add card has to retry the ATTACH — re-confirming a
 * spent intent just fails again and the admin has to cancel and start over.
 */
let confirmedPaymentMethodId: string | null = null;
const addFirstName = ref('');
const addLastName = ref('');
const addCardError = ref<string | null>(null);
const newCardComplete = ref(false);
// Sanctioned countries are not supported for billing (mirrors the retired update-card flow).
const BLOCKED_COUNTRIES = ['RU', 'UA', 'BY'];
const addCardCountry = ref<string | null>(null);
const isBlockedCountry = computed(() => BLOCKED_COUNTRIES.includes(addCardCountry.value ?? ''));
const canSubmitNewCard = computed(() => {
    const hasName = !!addFirstName.value.trim() && !!addLastName.value.trim();
    // Demo mode has no card element to complete and no country to block — it's a name away from done.
    return isDemoMode() ? hasName : hasName && newCardComplete.value && !isBlockedCountry.value;
});

/**
 * Keep the payer's cached primary-card display in step with what just changed.
 * A person's cache rides on /me; a company's lives on the billing summary, which
 * belongs to the page that opened this — so the close payload tells it to refetch.
 */
async function markLoadedIfRead(): Promise<void> {
    if (methodsError.value === null) hasLoaded.value = true;
}

async function refreshOwnerDisplay(): Promise<void> {
    if (isCompany.value) {
        cardsChanged.value = true;
        return;
    }
    await authStore.getMe();
}

async function retryLoad(): Promise<void> {
    // Blocked while it runs. Leaving `hasLoaded` true puts the STALE list back on
    // screen the moment the error clears — fully interactive — so an admin can delete
    // a card that is already gone, or open Add against a wallet the refresh is about
    // to report at its cap.
    hasLoaded.value = false;
    await fetchPaymentMethods();
    await markLoadedIfRead();
}

function closeDialog() {
    // A company's cards reach the billing page only through this payload, so closing
    // mid-write would strand the change: the request lands, `cardsChanged` flips, and
    // there is no longer anyone to tell. Refuse to close while a mutation is in
    // flight — the buttons are disabled to match, so this is the backstop.
    if (busy.value) return;
    dialogRef.value.close({ updated: cardsChanged.value });
}

// The opener hears every change while it is out (`onWriting`): its unmount would close this
// modal past the refusal above, and whoever made the change would never see whether it landed.
// The teardown releases it.
watch(busy, (writing) => dialogRef.value?.data?.onWriting?.(writing));

async function onSetPrimary(id: string) {
    busy.value = true;
    try {
        await setPrimary(id);
        await refreshOwnerDisplay();
        showSuccess('Success', 'Primary card updated.');
    } catch (e: unknown) {
        // The PUT may have COMMITTED and only lost its answer. Read the wallet back:
        // the modal would otherwise close with `updated: false`, and the financial page
        // would keep showing the old primary as the card that pays the next invoice.
        try {
            await fetchPaymentMethods();
            if (methodsError.value === null && methods.value.some((m: PaymentMethod) => m.id === id && m.is_primary)) {
                await refreshOwnerDisplay();
                showSuccess('Success', 'Primary card updated.');

                return;
            }
        } catch {
            // Still unreachable: report the failure below.
        }

        showError('Error', memberFacingMessage(e, 'Could not set primary card.'));
    } finally {
        busy.value = false;
    }
}

function onEdit(id: string) {
    const m = methods.value.find((x) => x.id === id);
    if (!m) return;
    editMonth.value = m.exp_month ?? null;
    editYear.value = m.exp_year ?? null;
    editName.value = m.name ?? '';
    // Tear down any in-progress add-card flow (Stripe elements + newCardComplete) before switching
    // to edit — same path as Cancel — so a later re-open of Add can't submit the abandoned
    // SetupIntent/card with a stale "complete" flag.
    cancelAdd();
    editingId.value = id;
    // The kebab item that opened this is gone; without the move, focus falls to <body> and the
    // editor that just appeared goes unnoticed.
    nextTick(() => editMonthEl.value?.focus());
}

async function onSaveEdit() {
    if (!editingId.value || busy.value) return;
    const payload: Record<string, unknown> = {};
    if (editMonth.value) payload.exp_month = editMonth.value;
    if (editYear.value) payload.exp_year = editYear.value;
    if (editName.value) payload.cardholder_name = editName.value;
    busy.value = true;
    try {
        await edit(editingId.value, payload);
        showSuccess('Success', 'Card updated.');
        editingId.value = null;
    } catch (e: unknown) {
        showError('Error', memberFacingMessage(e, 'Could not update card.'));
    } finally {
        busy.value = false;
    }
}

// The panel confirms the delete inline (in-row), so this just performs the removal.
async function onDelete(id: string) {
    busy.value = true;
    try {
        await remove(id);
        await refreshOwnerDisplay();
        showSuccess('Success', 'Payment method removed.');
    } catch (e: unknown) {
        // The delete may have COMMITTED and only lost its answer. Read the wallet back:
        // leaving the row on screen keeps a card the company no longer has in front of
        // whoever pays the next invoice, and a retry can only answer 404.
        try {
            await fetchPaymentMethods();
            if (methodsError.value === null && !methods.value.some((m: PaymentMethod) => m.id === id)) {
                await refreshOwnerDisplay();
                showSuccess('Success', 'Payment method removed.');

                return;
            }
        } catch {
            // Still unreachable: report the failure below with the row intact.
        }

        showError('Error', memberFacingMessage(e, 'Could not remove payment method.'));
    } finally {
        busy.value = false;
    }
}

// ── Inline add-card (Stripe) ──────────────────────────────────────────────
let stripe: any = null;
let elements: any = null;
let cardElement: any = null;
// Bumped on every setupStripeAddForm() so a superseded run (add cancelled then re-opened while the
// first was still awaiting the setup-intent/Stripe import) can detect it lost the race and bow out.
let setupGeneration = 0;

function onAdd() {
    if (atCap.value) return; // panel disables Add at the cap (D-23); guard anyway.
    editingId.value = null;
    addCardError.value = null;
    addFirstName.value = personStore.person?.first_name || '';
    addLastName.value = personStore.person?.last_name || '';
    adding.value = true;
    // Demo mode never talks to Stripe: no SetupIntent, no elements, no card element to mount.
    if (!isDemoMode()) {
        nextTick(setupStripeAddForm);
    }
}

function cancelAdd() {
    adding.value = false;
    // Cancel abandons the confirmed card too: a reopened form must confirm the card
    // actually typed into it, not attach the one left behind.
    confirmedPaymentMethodId = null;
    teardownStripeAddForm();
}

/**
 * An empty wallet opens its own add form, through `onAdd` so the prefilled names,
 * the demo-mode branch and the setup generation are the ones the button gets.
 *
 * Watched rather than done once after the first read, because the wallet also
 * empties while the modal is open — a retry that finally answers, and the last
 * card being removed. Fires only on the transition into that state, and the
 * `adding` guard is why a form already open is never re-minted underneath itself.
 */
watch(noCardsOnFile, (empty) => {
    if (empty && !adding.value) onAdd();
});

async function setupStripeAddForm() {
    addCardError.value = null;
    const generation = ++setupGeneration;
    if (!config.public.pb.stripeKey) {
        addCardError.value = 'Stripe is not configured.';
        return;
    }
    const id = ownerId.value;
    if (!id) {
        addCardError.value = isCompany.value ? 'No company loaded.' : 'No account loaded.';
        return;
    }

    // Mint an off-session SetupIntent (cap-checked server-side, D-23). Driving the element with
    // its client secret means confirming runs SCA/3DS NOW and authorizes the card for future
    // off-session renewals (W-1) — a plain createPaymentMethod would skip that.
    const answer = await usePbApi<{ data: { client_secret: string } }>(
        `/${ownerScope.value}/${id}/financial/payment-methods/setup-intent`,
        { method: 'POST' },
    );
    // Superseded while awaiting the setup-intent request (cancelled + re-opened) — a newer run
    // already owns the form; don't let this stale error clobber its state.
    if (generation !== setupGeneration) return;
    const clientSecret = answer.data?.data?.client_secret;
    if (answer.status < 200 || answer.status >= 300 || !clientSecret) {
        // Keep the add form open so this error stays visible — addCardError only renders inside the
        // form (v-if="adding"), so hiding the form here would make it vanish with no explanation.
        addCardError.value = memberFacingMessage(answer, 'Could not start card setup.');
        return;
    }

    const stripeModule = await import('@stripe/stripe-js');
    const loadedStripe = await stripeModule.loadStripe(config.public.pb.stripeKey);
    // Superseded by a newer setup while awaiting the Stripe import/load → bow out BEFORE touching
    // the shared `stripe` ref. Assigning it here would clobber the newer run's Stripe instance out
    // from under its already-created elements/cardElement, breaking confirmSetup's "same Stripe
    // instance" requirement even though the generation check below would still catch the mount step.
    if (generation !== setupGeneration) return;
    if (!loadedStripe) {
        addCardError.value = 'Could not initialize Stripe.';
        return;
    }
    stripe = loadedStripe;

    // The user may have cancelled/closed the add form while we awaited the setup-intent request and
    // the Stripe import; its mount node is then gone (v-if="adding"). Abort cleanly rather than
    // mounting the Payment Element into a detached selector, which throws.
    if (!adding.value || !document.getElementById('manage-pm-card-element')) {
        teardownStripeAddForm();
        return;
    }

    // Cards + Apple/Google Pay only: the SetupIntent is card-typed (drops US bank account); turn
    // Link off. Apple/Google Pay are card-backed wallets, so 'auto' still surfaces them where the
    // browser/device supports them (Google Pay on Chrome; Apple Pay on Safari w/ domain registered).
    // billingDetails.name is collected by our own fields and supplied at confirm time.
    elements = stripe.elements({ clientSecret });
    cardElement = elements.create('payment', {
        fields: { billingDetails: { name: 'never' } },
        wallets: { googlePay: 'auto', applePay: 'auto', link: 'never' },
    });
    cardElement.mount('#manage-pm-card-element');
    cardElement.on('change', (e: any) => {
        newCardComplete.value = e.complete;
        // Block cards billed to sanctioned countries. The Payment Element surfaces the selected
        // country in its change event; disable submit + surface the reason rather than confirming.
        const country = e?.value?.billingDetails?.address?.country as string | undefined;
        addCardCountry.value = country ?? null;
        addCardError.value = country && BLOCKED_COUNTRIES.includes(country)
            ? 'This country is not supported.'
            : null;
    });
}

function teardownStripeAddForm() {
    cardElement?.unmount?.();
    cardElement = null;
    elements = null;
    stripe = null;
    newCardComplete.value = false;
    addCardCountry.value = null;
}

async function onSaveNewCard() {
    if (!canSubmitNewCard.value || busy.value) return;
    // A held confirmed card needs no Stripe instance: only the attach is retried.
    if (!isDemoMode() && !confirmedPaymentMethodId && (!stripe || !elements)) return;
    busy.value = true;
    try {
        if (isDemoMode()) {
            // No SetupIntent, no Stripe SDK: the fixture layer adds the card straight to its own
            // state, so a fake client_secret never reaches Stripe's real elements()/confirmSetup().
            const answer = await usePbApi(`/${ownerScope.value}/${ownerId.value}/financial/payment-methods`, {
                method: 'POST',
                body: { name: `${addFirstName.value} ${addLastName.value}`.trim() },
            });
            if (answer.status < 200 || answer.status >= 300) {
                addCardError.value = memberFacingMessage(answer, 'Could not add card.');
                return;
            }
            await fetchPaymentMethods();
        } else {
            const { error: submitError } = confirmedPaymentMethodId
                ? { error: null }
                : await elements.submit();
            if (submitError) {
                addCardError.value = submitError.message || 'Card details are incomplete.';
                return;
            }
            // Confirm the SetupIntent — runs 3DS if the issuer requires it, attaches the PM, and
            // records the off-session mandate. redirect:'if_required' keeps the card challenge inline.
            const { error, setupIntent } = confirmedPaymentMethodId
                ? { error: null, setupIntent: null }
                : await stripe.confirmSetup({
                elements,
                confirmParams: {
                    return_url: window.location.href,
                    payment_method_data: { billing_details: { name: `${addFirstName.value} ${addLastName.value}`.trim() } },
                },
                redirect: 'if_required',
            });
            if (error) {
                addCardError.value = error.message || 'Could not add card.';
                return;
            }
            const pmId = confirmedPaymentMethodId ?? (typeof setupIntent?.payment_method === 'string'
                ? setupIntent.payment_method
                : setupIntent?.payment_method?.id);
            if (!pmId) {
                addCardError.value = 'Could not add card.';
                return;
            }
            // Held so a failed attach is retried as an attach, not a re-confirm.
            confirmedPaymentMethodId = pmId;
            // Server-side bookkeeping (cap/dedup/set-primary) + refetch; throws (surfaced here) on 422.
            await add(pmId);
            confirmedPaymentMethodId = null;
        }
        await refreshOwnerDisplay();
        showSuccess('Success', 'Payment method added.');
        cancelAdd();
    } catch (e: unknown) {
        // The attach may have COMMITTED and only lost its answer. Read the wallet back
        // before reporting a failure: the collection dedups on fingerprint, so every
        // retry would answer "already on file" while the saved card sat there unseen
        // until the modal was closed and reopened.
        if (confirmedPaymentMethodId) {
            try {
                await fetchPaymentMethods();
                if (methods.value.some((m: PaymentMethod) => m.id === confirmedPaymentMethodId)) {
                    confirmedPaymentMethodId = null;
                    // Owner-aware, exactly as the ordinary success path: for a company
                    // this is what sets `cardsChanged`, and without it the financial
                    // page skips its refresh and keeps showing "Not set" over a card
                    // the company now has.
                    await refreshOwnerDisplay();
                    showSuccess('Success', 'Payment method added.');
                    cancelAdd();

                    return;
                }
            } catch {
                // Still unreachable: fall through to the error, which keeps the
                // confirmed method so Save retries the attach rather than the confirm.
            }
        }

        addCardError.value = memberFacingMessage(e, 'Could not add card.');
    } finally {
        busy.value = false;
    }
}

/** False from the moment this modal is torn down: nothing may resume after it. */
let live = true;

onMounted(async () => {
    await fetchPaymentMethods();
    if (!live) return;   // closed while the wallet was loading
    // Marking the read as landed is what lets `noCardsOnFile` speak, and its watcher
    // opens the add form when the answer is an empty wallet.
    hasLoaded.value = methodsError.value === null;
});
onBeforeUnmount(() => {
    // Invalidate any setup still in flight, not just the element that got mounted:
    // a pending setup-intent request or Stripe import would otherwise continue into
    // a closed modal and mount into a node that no longer exists.
    //
    // `live` covers the one continuation that runs BEFORE any setup exists: the first
    // wallet read, still outstanding when the modal closed, reporting back into a
    // torn-down dialog.
    live = false;
    setupGeneration++;
    adding.value = false;
    confirmedPaymentMethodId = null;
    teardownStripeAddForm();
    dialogRef.value?.data?.onWriting?.(false);
});
</script>

<!-- Plain CSS on design tokens, independent of hive's custom Tailwind palette (which omits some shades). -->
<style scoped>
.manage-pm-modal {
    width: 91.6667%;
    margin: 0 auto;
    background: var(--surface);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
}
@media (min-width: 640px) {
    .manage-pm-modal { width: 100%; max-width: 36rem; }
}
.mpm-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 24px 24px 16px;
}
.mpm-title { display: flex; align-items: flex-start; gap: 12px; }
.mpm-icon {
    display: flex;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background: var(--surface-sunken);
}
.mpm-heading { color: var(--ink); font-size: 18px; font-weight: 600; line-height: 1.6; margin: 0; }
.mpm-sub { color: var(--ink-body); font-size: 14px; line-height: 1.4; margin: 2px 0 0; }
.mpm-close {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    color: var(--ink-faint);
    cursor: pointer;
    flex-shrink: 0;
    background: transparent;
    border: none;
}
.mpm-close:hover { color: var(--ink-body); }
/* The bottom padding was the footer's until the Done button went: the close X is the
   only way out, and without it here the last row sits on the modal's edge. */
.mpm-body { display: flex; flex-direction: column; gap: 16px; padding: 0 24px 24px; }
.mpm-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 4px;
    padding: 16px;
    border-radius: 10px;
    border: 1px solid var(--hairline);
    background: var(--canvas);
}
.mpm-load-error {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    font-size: 14px;
    color: var(--danger-ink);
}
.mpm-retry {
    font-size: 14px;
    font-weight: 600;
    color: var(--action);
    cursor: pointer;
}
.mpm-retry:hover {
    text-decoration: underline;
}
.mpm-form-title { font-size: 14px; font-weight: 600; color: var(--ink); margin: 0; }
.mpm-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
@media (min-width: 640px) {
    .mpm-grid { grid-template-columns: repeat(3, 1fr); }
}
.mpm-input {
    width: 100%;
    border: 1px solid var(--hairline-strong);
    border-radius: 6px;
    background: var(--surface);
    padding: 8px 12px;
    font-size: 14px;
    color: var(--ink);
}
.mpm-card-element {
    width: 100%;
    min-height: 44px;
    border: 1px solid var(--hairline-strong);
    border-radius: 6px;
    background: var(--surface);
    padding: 8px 12px;
}
.mpm-actions { display: flex; gap: 8px; justify-content: flex-end; }
</style>
